import React, { useState, useEffect, useCallback } from 'react';
import { userRateAPI, billingRateAPI, rateGroupAPI, projectAPI, userProfileAPI, UserWithRateCount } from '../services/api';
import type { UserRate, BillingRate, RateGroup, Project, UserProfile, EmployeeType } from '../types';
import api from '../services/api';
import Pagination from './Pagination';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
}

const PAGE_SIZE = 50;

const UserRateManagement: React.FC = () => {
  // Existing state
  const [userRates, setUserRates] = useState<UserRate[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [billingRates, setBillingRates] = useState<BillingRate[]>([]);
  const [rateGroups, setRateGroups] = useState<RateGroup[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showGroupAssignment, setShowGroupAssignment] = useState(false);
  const [filters, setFilters] = useState({
    user: '',
    billing_rate: '',
    project: '',
  });
  const [newAssignment, setNewAssignment] = useState({
    user: '',
    billing_rate: '',
  });
  const [groupAssignment, setGroupAssignment] = useState({
    user: '',
    rate_group: '',
  });
  const [selectedGroup, setSelectedGroup] = useState<RateGroup | null>(null);
  
  // Employee Types state
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);
  const [showEmployeeTypes, setShowEmployeeTypes] = useState(true);
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState<string>('all');
  const [updatingProfile, setUpdatingProfile] = useState<number | null>(null);

  // New state for grouped view
  const [usersWithRates, setUsersWithRates] = useState<UserWithRateCount[]>([]);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const [expandedUserRates, setExpandedUserRates] = useState<Map<number, UserRate[]>>(new Map());
  const [loadingUserRates, setLoadingUserRates] = useState<Set<number>>(new Set());

  // Pagination state
  const [groupedPage, setGroupedPage] = useState(1);
  const [groupedTotalPages, setGroupedTotalPages] = useState(1);
  const [groupedTotalItems, setGroupedTotalItems] = useState(0);
  const [flatPage, setFlatPage] = useState(1);
  const [flatTotalPages, setFlatTotalPages] = useState(1);
  const [flatTotalItems, setFlatTotalItems] = useState(0);

  // Check if any filter is applied
  const hasFilters = filters.user || filters.billing_rate || filters.project;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (groupAssignment.rate_group) {
      const group = rateGroups.find(g => g.id === parseInt(groupAssignment.rate_group));
      setSelectedGroup(group || null);
    } else {
      setSelectedGroup(null);
    }
  }, [groupAssignment.rate_group, rateGroups]);

  // When filters change, reset pagination and fetch appropriate data
  useEffect(() => {
    if (hasFilters) {
      setFlatPage(1);
      fetchUserRates(1);
    } else {
      setGroupedPage(1);
      fetchUsersSummary(1);
    }
  }, [filters]);

  // When grouped page changes, fetch users summary
  useEffect(() => {
    if (!hasFilters) {
      fetchUsersSummary(groupedPage);
    }
  }, [groupedPage]);

  // When flat page changes, fetch user rates
  useEffect(() => {
    if (hasFilters) {
      fetchUserRates(flatPage);
    }
  }, [flatPage]);

  const fetchData = async () => {
    try {
      const [usersRes, ratesRes, groupsRes, projectsRes, profilesRes] = await Promise.all([
        api.get<{results: User[]}>('/auth/users/'),
        billingRateAPI.getAll(),
        rateGroupAPI.getAll(),
        projectAPI.getAll(),
        userProfileAPI.getAll(),
      ]);
      setUsers(usersRes.data.results || usersRes.data);
      setBillingRates(ratesRes.data.results.filter(r => r.is_active));
      setRateGroups(groupsRes.data.results.filter(g => g.is_active));
      setProjects(projectsRes.data.results);
      setUserProfiles(profilesRes.data.results || []);
      
      // Fetch initial data based on filter state
      if (hasFilters) {
        await fetchUserRates(1);
      } else {
        await fetchUsersSummary(1);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEmployeeTypeChange = async (profileId: number, newType: EmployeeType) => {
    setUpdatingProfile(profileId);
    try {
      await userProfileAPI.update(profileId, { employee_type: newType });
      // Update local state
      setUserProfiles(prev => prev.map(p => 
        p.id === profileId ? { ...p, employee_type: newType } : p
      ));
    } catch (error) {
      console.error('Failed to update employee type:', error);
      alert('Failed to update employee type');
    } finally {
      setUpdatingProfile(null);
    }
  };
  
  const getFilteredProfiles = () => {
    if (employeeTypeFilter === 'all') return userProfiles;
    return userProfiles.filter(p => p.employee_type === employeeTypeFilter);
  };
  
  const getEmployeeTypeCounts = () => {
    const hourly = userProfiles.filter(p => p.employee_type === 'hourly').length;
    const salaried = userProfiles.filter(p => p.employee_type === 'salaried').length;
    return { hourly, salaried, total: userProfiles.length };
  };

  const fetchUsersSummary = useCallback(async (page: number) => {
    try {
      const response = await userRateAPI.getUsersSummary({ page, page_size: PAGE_SIZE });
      setUsersWithRates(response.data.results);
      setGroupedTotalItems(response.data.count);
      setGroupedTotalPages(Math.ceil(response.data.count / PAGE_SIZE));
    } catch (error) {
      console.error('Failed to fetch users summary:', error);
    }
  }, []);

  const fetchUserRates = useCallback(async (page: number) => {
    try {
      const params: any = { page, page_size: PAGE_SIZE };
      if (filters.user) params.user = filters.user;
      if (filters.billing_rate) params.billing_rate = filters.billing_rate;
      if (filters.project) params.project = filters.project;
      
      const userRatesRes = await userRateAPI.getAll(params);
      setUserRates(userRatesRes.data.results);
      setFlatTotalItems(userRatesRes.data.count);
      setFlatTotalPages(Math.ceil(userRatesRes.data.count / PAGE_SIZE));
    } catch (error) {
      console.error('Failed to fetch user rates:', error);
    }
  }, [filters]);

  const fetchRatesForUser = async (userId: number) => {
    if (expandedUserRates.has(userId)) return;
    
    setLoadingUserRates(prev => new Set(prev).add(userId));
    try {
      const response = await userRateAPI.getAll({ user: userId });
      setExpandedUserRates(prev => new Map(prev).set(userId, response.data.results));
    } catch (error) {
      console.error('Failed to fetch rates for user:', error);
    } finally {
      setLoadingUserRates(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const toggleUserExpanded = (userId: number) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
        // Fetch rates for this user if not already fetched
        fetchRatesForUser(userId);
      }
      return newSet;
    });
  };

  const clearFilters = () => {
    setFilters({ user: '', billing_rate: '', project: '' });
    setExpandedUsers(new Set());
    setExpandedUserRates(new Map());
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to remove this user-rate assignment?')) {
      try {
        await userRateAPI.delete(id);
        // Refresh data
        if (hasFilters) {
          fetchUserRates(flatPage);
        } else {
          // Clear expanded user rates cache and refetch summary
          setExpandedUserRates(new Map());
          fetchUsersSummary(groupedPage);
        }
      } catch (error) {
        console.error('Failed to delete user-rate:', error);
        alert('Failed to delete assignment');
      }
    }
  };

  const handleAddAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await userRateAPI.create({
        user: parseInt(newAssignment.user),
        billing_rate: parseInt(newAssignment.billing_rate),
      });
      setShowAddForm(false);
      setNewAssignment({ user: '', billing_rate: '' });
      // Refresh data
      setExpandedUserRates(new Map());
      if (hasFilters) {
        fetchUserRates(flatPage);
      } else {
        fetchUsersSummary(groupedPage);
      }
      alert('User successfully assigned to billing rate!');
    } catch (error: any) {
      console.error('Failed to create assignment:', error);
      let errorMsg = 'Failed to create assignment.';
      
      if (error.response?.data) {
        if (error.response.data.non_field_errors) {
          errorMsg = error.response.data.non_field_errors.join(' ');
        } else if (error.response.data.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        }
      }
      
      alert(errorMsg);
    }
  };

  const handleGroupAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await rateGroupAPI.assignToUser(
        parseInt(groupAssignment.rate_group),
        parseInt(groupAssignment.user)
      );
      setShowGroupAssignment(false);
      setGroupAssignment({ user: '', rate_group: '' });
      setSelectedGroup(null);
      // Refresh data
      setExpandedUserRates(new Map());
      if (hasFilters) {
        fetchUserRates(flatPage);
      } else {
        fetchUsersSummary(groupedPage);
      }
      alert(response.data.message);
    } catch (error: any) {
      console.error('Failed to assign rate group:', error);
      let errorMsg = 'Failed to assign rate group.';
      
      if (error.response?.data) {
        if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else if (error.response.data.detail) {
          errorMsg = error.response.data.detail;
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        }
      }
      
      alert(errorMsg);
    }
  };

  const renderGroupedView = () => {
    if (usersWithRates.length === 0) {
      return <p>No user-rate assignments found. Assign users to billing rates to get started.</p>;
    }

    return (
      <>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>User</th>
                <th>Assigned Rates</th>
              </tr>
            </thead>
            <tbody>
              {usersWithRates.map((user) => (
                <React.Fragment key={user.id}>
                  <tr 
                    onClick={() => toggleUserExpanded(user.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span style={{ fontSize: '1.2rem' }}>
                        {expandedUsers.has(user.id) ? '▼' : '▶'}
                      </span>
                    </td>
                    <td>
                      <strong>{user.username}</strong>
                      {user.full_name !== user.username && (
                        <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                          {user.full_name}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{
                        backgroundColor: '#ecf6fc',
                        color: '#3498db',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                      }}>
                        {user.rates_count} rate{user.rates_count !== 1 ? 's' : ''}
                      </span>
                    </td>
                  </tr>
                  {expandedUsers.has(user.id) && (
                    <tr>
                      <td colSpan={3} style={{ padding: '0', backgroundColor: '#f8f9fa' }}>
                        <div style={{ padding: '1rem 1rem 1rem 3rem' }}>
                          {loadingUserRates.has(user.id) ? (
                            <p style={{ color: '#7f8c8d', margin: 0 }}>Loading rates...</p>
                          ) : expandedUserRates.get(user.id)?.length ? (
                            <table style={{ marginBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th>Project</th>
                                  <th>Billing Rate</th>
                                  <th>Amount</th>
                                  <th>Assigned Date</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {expandedUserRates.get(user.id)!.map((rate) => (
                                  <tr key={rate.id}>
                                    <td>
                                      <span style={{ 
                                        color: '#3498db', 
                                        fontWeight: 'bold',
                                        backgroundColor: '#ecf6fc',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                      }}>
                                        {rate.project_name}
                                      </span>
                                    </td>
                                    <td>{rate.billing_rate_name}</td>
                                    <td>${rate.billing_rate_details?.rate_amount}/hr</td>
                                    <td>{rate.assigned_at ? new Date(rate.assigned_at).toLocaleDateString() : '-'}</td>
                                    <td className="actions">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(rate.id!);
                                        }}
                                        className="btn btn-danger btn-small"
                                      >
                                        Remove
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p style={{ color: '#7f8c8d', margin: 0 }}>No rates found.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={groupedPage}
          totalPages={groupedTotalPages}
          totalItems={groupedTotalItems}
          pageSize={PAGE_SIZE}
          onPageChange={setGroupedPage}
        />
      </>
    );
  };

  const renderFlatView = () => {
    if (userRates.length === 0) {
      return <p>No user-rate assignments found. Try adjusting your filters.</p>;
    }

    return (
      <>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Project</th>
                <th>Billing Rate</th>
                <th>Amount</th>
                <th>Assigned By</th>
                <th>Assigned Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userRates.map((userRate) => (
                <tr key={userRate.id}>
                  <td>
                    <strong>{userRate.user_username}</strong>
                    {userRate.user_full_name && (
                      <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                        {userRate.user_full_name}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{ 
                      color: '#3498db', 
                      fontWeight: 'bold',
                      backgroundColor: '#ecf6fc',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                    }}>
                      {userRate.project_name}
                    </span>
                  </td>
                  <td>{userRate.billing_rate_name}</td>
                  <td>${userRate.billing_rate_details?.rate_amount}/hr</td>
                  <td>{userRate.assigned_by_username || '-'}</td>
                  <td>{userRate.assigned_at ? new Date(userRate.assigned_at).toLocaleDateString() : '-'}</td>
                  <td className="actions">
                    <button
                      onClick={() => handleDelete(userRate.id!)}
                      className="btn btn-danger btn-small"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={flatPage}
          totalPages={flatTotalPages}
          totalItems={flatTotalItems}
          pageSize={PAGE_SIZE}
          onPageChange={setFlatPage}
        />
      </>
    );
  };

  if (loading) return <div className="container">Loading...</div>;

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>User Rate Assignments</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => {
                setShowAddForm(!showAddForm);
                setShowGroupAssignment(false);
              }}
              className="btn btn-primary"
            >
              {showAddForm ? 'Cancel' : 'Assign Single Rate'}
            </button>
            <button
              onClick={() => {
                setShowGroupAssignment(!showGroupAssignment);
                setShowAddForm(false);
              }}
              className="btn btn-success"
            >
              {showGroupAssignment ? 'Cancel' : 'Assign Rate Group'}
            </button>
          </div>
        </div>

        {/* Employee Types Section */}
        <div style={{
          marginBottom: '1.5rem',
          border: '2px solid #ff9800',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <button
            onClick={() => setShowEmployeeTypes(!showEmployeeTypes)}
            style={{
              width: '100%',
              padding: '1rem',
              backgroundColor: '#fff3e0',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '1rem',
              fontWeight: 'bold',
              color: '#e65100',
            }}
          >
            <span>
              {showEmployeeTypes ? '▼' : '▶'} Employee Types
              <span style={{ fontWeight: 'normal', marginLeft: '1rem', color: '#666' }}>
                ({getEmployeeTypeCounts().hourly} hourly, {getEmployeeTypeCounts().salaried} salaried)
              </span>
            </span>
            <span style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
              {showEmployeeTypes ? 'Click to collapse' : 'Click to expand'}
            </span>
          </button>
          
          {showEmployeeTypes && (
            <div style={{ padding: '1rem', backgroundColor: '#fffde7' }}>
              {/* Filter */}
              <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <label style={{ fontWeight: 'bold' }}>Filter:</label>
                <select
                  value={employeeTypeFilter}
                  onChange={(e) => setEmployeeTypeFilter(e.target.value)}
                  style={{ padding: '0.5rem' }}
                >
                  <option value="all">All Employees</option>
                  <option value="hourly">Hourly Only</option>
                  <option value="salaried">Salaried Only</option>
                </select>
              </div>
              
              {/* User List */}
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                backgroundColor: 'white',
                borderRadius: '4px',
                border: '1px solid #ddd',
              }}>
                <table style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>User</th>
                      <th style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>Employee Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredProfiles().map((profile) => (
                      <tr 
                        key={profile.id}
                        style={{
                          backgroundColor: profile.employee_type === 'hourly' ? '#fff8e1' : 'transparent',
                        }}
                      >
                        <td>
                          <strong>{profile.user_username}</strong>
                          {profile.user_full_name && profile.user_full_name !== profile.user_username && (
                            <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                              ({profile.user_full_name})
                            </span>
                          )}
                        </td>
                        <td>
                          <select
                            value={profile.employee_type}
                            onChange={(e) => handleEmployeeTypeChange(profile.id!, e.target.value as EmployeeType)}
                            disabled={updatingProfile === profile.id}
                            style={{
                              padding: '0.4rem 0.8rem',
                              borderRadius: '4px',
                              border: profile.employee_type === 'hourly' 
                                ? '2px solid #ff9800' 
                                : '1px solid #ccc',
                              backgroundColor: profile.employee_type === 'hourly' 
                                ? '#fff3e0' 
                                : 'white',
                              fontWeight: profile.employee_type === 'hourly' ? 'bold' : 'normal',
                              cursor: updatingProfile === profile.id ? 'wait' : 'pointer',
                            }}
                          >
                            <option value="salaried">Salaried</option>
                            <option value="hourly">Hourly</option>
                          </select>
                          {updatingProfile === profile.id && (
                            <span style={{ marginLeft: '0.5rem', color: '#666' }}>Saving...</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <p style={{ 
                marginTop: '0.75rem', 
                marginBottom: 0, 
                fontSize: '0.85rem', 
                color: '#666' 
              }}>
                Hourly employees will see biweekly timesheet options for payroll submission.
              </p>
            </div>
          )}
        </div>

        {showAddForm && (
          <div style={{ 
            padding: '1.5rem', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '4px', 
            marginBottom: '1.5rem' 
          }}>
            <h3 style={{ marginBottom: '1rem' }}>New Assignment</h3>
            <form onSubmit={handleAddAssignment} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="user">User</label>
                <select
                  id="user"
                  value={newAssignment.user}
                  onChange={(e) => setNewAssignment({ ...newAssignment, user: e.target.value })}
                  required
                >
                  <option value="">Select a user</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.first_name} {user.last_name})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="billing_rate">Billing Rate</label>
                <select
                  id="billing_rate"
                  value={newAssignment.billing_rate}
                  onChange={(e) => setNewAssignment({ ...newAssignment, billing_rate: e.target.value })}
                  required
                >
                  <option value="">Select a billing rate</option>
                  {billingRates.map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.project_code} - {rate.name} (${rate.rate_amount}/hr)
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn-success">
                Assign
              </button>
            </form>
          </div>
        )}

        {showGroupAssignment && (
          <div style={{ 
            padding: '1.5rem', 
            backgroundColor: '#e8f5e9', 
            borderRadius: '4px', 
            marginBottom: '1.5rem',
            border: '2px solid #4caf50'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Assign Rate Group to User</h3>
            <form onSubmit={handleGroupAssignment}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="group_user">User</label>
                  <select
                    id="group_user"
                    value={groupAssignment.user}
                    onChange={(e) => setGroupAssignment({ ...groupAssignment, user: e.target.value })}
                    required
                  >
                    <option value="">Select a user</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.username} ({user.first_name} {user.last_name})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="rate_group">Rate Group</label>
                  <select
                    id="rate_group"
                    value={groupAssignment.rate_group}
                    onChange={(e) => setGroupAssignment({ ...groupAssignment, rate_group: e.target.value })}
                    required
                  >
                    <option value="">Select a rate group</option>
                    {rateGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.rates_count} rates)
                      </option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="btn btn-success">
                  Assign Group
                </button>
              </div>

              {selectedGroup && selectedGroup.rate_group_memberships && (
                <div style={{ 
                  backgroundColor: 'white',
                  padding: '1rem',
                  borderRadius: '4px',
                  border: '1px solid #4caf50'
                }}>
                  <h4 style={{ marginBottom: '0.5rem', color: '#2e7d32' }}>
                    Preview: {selectedGroup.rates_count} rate(s) will be assigned
                  </h4>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {selectedGroup.rate_group_memberships.map((membership) => (
                      <div 
                        key={membership.id}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#f1f8e9',
                          borderRadius: '4px',
                          fontSize: '0.9rem'
                        }}
                      >
                        <strong>{membership.project_code} - {membership.billing_rate_name}</strong> • {membership.project_name}
                      </div>
                    ))}
                  </div>
                  <p style={{ marginTop: '0.75rem', marginBottom: 0, fontSize: '0.85rem', color: '#666' }}>
                    Note: Rates already assigned to the user will be skipped.
                  </p>
                </div>
              )}
            </form>
          </div>
        )}

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              User
            </label>
            <select
              value={filters.user}
              onChange={(e) => setFilters({ ...filters, user: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.username} ({user.first_name} {user.last_name})</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Billing Rate
            </label>
            <select
              value={filters.billing_rate}
              onChange={(e) => setFilters({ ...filters, billing_rate: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All Rates</option>
              {billingRates.map((rate) => (
                <option key={rate.id} value={rate.id}>{rate.project_code} - {rate.name}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Project
            </label>
            <select
              value={filters.project}
              onChange={(e) => setFilters({ ...filters, project: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.code} - {project.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={clearFilters}
              className="btn btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* View mode indicator */}
        {hasFilters && (
          <div style={{ 
            marginBottom: '1rem', 
            padding: '0.5rem 1rem', 
            backgroundColor: '#fff3cd', 
            borderRadius: '4px',
            color: '#856404',
            fontSize: '0.9rem'
          }}>
            Showing filtered results. <button 
              onClick={clearFilters}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#0056b3', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                padding: 0,
                font: 'inherit'
              }}
            >
              Clear filters
            </button> to see grouped view by user.
          </div>
        )}

        {/* Render appropriate view */}
        {hasFilters ? renderFlatView() : renderGroupedView()}
      </div>
    </div>
  );
};

export default UserRateManagement;
